// pages/TransfertsPage.tsx - Version corrigée
import { useState } from "react";
import { DollarSign, Banknote, RefreshCw, AlertCircle, CheckCircle, Clock, TrendingUp } from "lucide-react";

import useTransfertsCanadaCmr from "../../../../hooks/administration/virements-bancaires/useTransfertsCanadaCmr";
import { BankTransfertItem } from "../../../../types/features/administration/VirementBancaire";
import { alertError } from "../../../../helpers/alertError";
import { confirmModal } from "../../../../helpers/confirmModal";
import { bankService } from "../../../../services/administration/bankService";
import TransfertList from "../../../../components/features/administration/virements-bacaires/TransfertList";
import TransfertDetailsModal from "../../../../components/features/administration/virements-bacaires/TransfertDetailsModal";
import ChangeStatusModal from "../../../../components/features/administration/virements-bacaires/ChangeStatusModal";
import VerifyKeyModal from "../../../../components/features/administration/virements-bacaires/VerifyKeyModal";
import BankTransfertsLayout from "../../../../layouts/BankTransfertsLayout";

interface TransfertsPageProps {
  onDirectionChange: (direction: string) => void;
  currentDirection?: string;
}

export default function VirementsByDirectionPage({ currentDirection, onDirectionChange }: TransfertsPageProps) {
  // États pour les modals
  const [selectedTransfert, setSelectedTransfert] = useState<BankTransfertItem | null>(null);
  const [modalType, setModalType] = useState<"details" | "checkStatus" | "changeStatus" | "verifyKey" | null>(null);

  // États pour les chargements des actions
  const [loadingStates, setLoadingStates] = useState({
    complete: "",
    inProgress: "",
    fail: "",
    cancel: "",
  });

  const { bankTransfertsItems, loading, error, refetch, stats } = useTransfertsCanadaCmr({
    limit: 50,
    direction: currentDirection,
    autoFetch: true,
  });

  const getToken = (): string | null => {
    const token = localStorage.getItem("token");
    if (!token) return null;
    return token.replace(/^"|"$/g, "").trim();
  };

  // ========== ACTIONS CENTRALISÉES ==========

  const handleViewDetails = (item: BankTransfertItem) => {
    setSelectedTransfert(item);
    setModalType("details");
  };

  const handleCheckStatus = (item: BankTransfertItem) => {
    setSelectedTransfert(item);
    setModalType("checkStatus");
  };

  const handleChangeStatus = (item: BankTransfertItem) => {
    setSelectedTransfert(item);
    setModalType("changeStatus");
  };

  const handleVerifyKey = (item: BankTransfertItem) => {
    setSelectedTransfert(item);
    setModalType("verifyKey");
  };

  // extension_id = item.details.id
  const handleComplete = async (item: BankTransfertItem) => {
    const token = getToken();
    if (!token) {
      alertError("❌ Token non trouvé. Veuillez vous reconnecter.");
      return;
    }

    const confirmed = await confirmModal("Êtes-vous sûr de vouloir marquer ce payout comme COMPLETED ?");
    if (!confirmed) return;

    setLoadingStates((prev) => ({ ...prev, complete: item.details.id }));
    try {
      await bankService.completePayout(token, item.details.id);
      alert("✅ Payout marqué comme COMPLETED avec succès !");
      await refetch();
    } catch (err: any) {
      alertError(`❌ Erreur: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoadingStates((prev) => ({ ...prev, complete: "" }));
    }
  };

  // extension_id = item.details.id
  const handleInProgress = async (item: BankTransfertItem) => {
    const token = getToken();
    if (!token) {
      alertError("❌ Token non trouvé. Veuillez vous reconnecter.");
      return;
    }

    const confirmed = await confirmModal("Êtes-vous sûr de vouloir marquer ce payout comme IN_PROGRESS ?");
    if (!confirmed) return;

    setLoadingStates((prev) => ({ ...prev, inProgress: item.details.id }));
    try {
      await bankService.inProgressPayout(token, item.details.id);
      alert("⏳ Payout marqué comme IN_PROGRESS avec succès !");
      await refetch();
    } catch (err: any) {
      alertError(`❌ Erreur: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoadingStates((prev) => ({ ...prev, inProgress: "" }));
    }
  };

  // extension_id = item.details.id
  const handleFail = async (item: BankTransfertItem) => {
    const token = getToken();
    if (!token) {
      alertError("❌ Token non trouvé. Veuillez vous reconnecter.");
      return;
    }

    const confirmed = await confirmModal("Êtes-vous sûr de vouloir marquer ce payout comme FAILED ?");
    if (!confirmed) return;

    setLoadingStates((prev) => ({ ...prev, fail: item.details.id }));
    try {
      await bankService.failPayout(token, item.details.id);
      alert("❌ Payout marqué comme FAILED avec succès !");
      await refetch();
    } catch (err: any) {
      alertError(`❌ Erreur: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoadingStates((prev) => ({ ...prev, fail: "" }));
    }
  };

  // extension_id = item.details.id
  const handleCancel = async (item: BankTransfertItem) => {
    const token = getToken();
    if (!token) {
      alertError("❌ Token non trouvé. Veuillez vous reconnecter.");
      return;
    }

    const confirmed = await confirmModal("Êtes-vous sûr de vouloir annuler ce payout ?");
    if (!confirmed) return;

    setLoadingStates((prev) => ({ ...prev, cancel: item.details.id }));
    try {
      await bankService.cancelPayout(token, item.details.id);
      alert("🚫 Payout marqué comme CANCELED avec succès !");
      await refetch();
    } catch (err: any) {
      alertError(`❌ Erreur: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoadingStates((prev) => ({ ...prev, cancel: "" }));
    }
  };

  // Pour les changements de statut manuels : extension_id = item.details.id
  const handleManualStatusChange = async (item: BankTransfertItem, newStatus: string, newPayoutStatus: string) => {
    const token = getToken();
    if (!token) {
      alertError("❌ Token non trouvé.");
      return;
    }

    try {
      if (newStatus === "COMPLETED" && newPayoutStatus === "COMPLETED") {
        await bankService.completePayout(token, item.details.id);
      } else if (newStatus === "IN_PROGRESS" && newPayoutStatus === "IN_PROGRESS") {
        await bankService.inProgressPayout(token, item.details.id);
      } else if (newStatus === "FAILED" && newPayoutStatus === "FAILED") {
        await bankService.failPayout(token, item.details.id);
      } else if (newStatus === "CANCELLED" && newPayoutStatus === "CANCELLED") {
        await bankService.cancelPayout(token, item.details.id);
      } else {
        alert("⚠️ Combinaison de statuts non prise en charge");
        return;
      }
      alert("✅ Statut modifié avec succès !");
      await refetch();
      setModalType(null);
      setSelectedTransfert(null);
    } catch (err: any) {
      alertError(`❌ Erreur: ${err.response?.data?.detail || err.message}`);
    }
  };

  // verify-key utilise transfert_id = item.transfert.id
  const handleVerifyKeySubmit = async (
    item: BankTransfertItem,
    securityKey: string,
    accountNumber?: string,
    phoneNumber?: string
  ) => {
    const token = getToken();
    if (!token) {
      alertError("❌ Token non trouvé.");
      return;
    }

    try {
      const result = await bankService.verifySecurityKey(token, item.transfert.id, {
        security_key: securityKey,
        account_number: accountNumber,
        phone_number: phoneNumber,
      });

      if (result.success || result.valid) {
        alert("✅ Clé de sécurité vérifiée avec succès !");
        setModalType(null);
        setSelectedTransfert(null);
      } else {
        alertError(`❌ Vérification échouée: ${result.message || "Clé invalide"}`);
      }
    } catch (err: any) {
      alertError(`❌ Erreur: ${err.response?.data?.detail || err.message}`);
    }
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedTransfert(null);
  };

  if (error) {
    return (
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle size={48} className="mx-auto text-red-600 mb-3" />
            <p className="text-red-600 text-lg mb-3">Une erreur est survenue</p>
            <p className="text-red-500 text-sm mb-4">{error}</p>
            <button
              onClick={refetch}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 mx-auto"
            >
              <RefreshCw size={16} />
              Réessayer
            </button>
          </div>
        </div>
    );
  }

  return (
    <BankTransfertsLayout currentDirection={currentDirection} onDirectionChange={onDirectionChange}>
      <div className="max-w-6xl mx-auto px-4 py-6">
      

        {/* Deuxième ligne de stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Clock size={16} className="text-yellow-600" />
              Transferts en attente
            </div>
            <p className="text-3xl font-bold text-yellow-600">{stats.pendingCount}</p>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <CheckCircle size={16} className="text-green-600" />
              Transferts complétés
            </div>
            <p className="text-3xl font-bold text-green-600">{stats.completedCount}</p>
          </div>
        </div>

        {/* Liste des transferts */}
        <TransfertList
          transferts={bankTransfertsItems}
          loading={loading}
          onRefresh={refetch}
          onViewDetails={handleViewDetails}
          onCheckStatus={handleCheckStatus}
          onChangeStatus={handleChangeStatus}
          onVerifyKey={handleVerifyKey}
          onComplete={handleComplete}
          onInProgress={handleInProgress}
          onFail={handleFail}
          onCancel={handleCancel}
          loadingStates={loadingStates}
        />
      </div>

      {/* Modals */}
      {modalType === "details" && selectedTransfert && (
        <TransfertDetailsModal isOpen={true} onClose={closeModal} item={selectedTransfert} />
      )}

      {modalType === "changeStatus" && selectedTransfert && (
        <ChangeStatusModal
          isOpen={true}
          onClose={closeModal}
          transfertItem={selectedTransfert}
          onConfirm={handleManualStatusChange}
        />
      )}

      {modalType === "verifyKey" && selectedTransfert && (
        <VerifyKeyModal
          isOpen={true}
          onClose={closeModal}
          transfertItem={selectedTransfert}
          onVerify={handleVerifyKeySubmit}
        />
      )}
    </BankTransfertsLayout>
  );
}
